import _union from 'lodash/union';

import TYPES from '../action-types';
import * as helpers from '../helpers/notifications';

function dropPendingReadRollback(pendingReadRollbacks, rollbackId) {
  if (
    !pendingReadRollbacks
    || !rollbackId
    || !Object.prototype.hasOwnProperty.call(pendingReadRollbacks, rollbackId)
  ) {
    return pendingReadRollbacks || {};
  }
  const next = { ...pendingReadRollbacks };
  delete next[rollbackId];
  return next;
}

const defaultState = {
  items: [],
  changedTags: [],
  jobsStatus: {},
  newComments: {},
  newRevisions: {},
  notificationsUnreadCount: 0,
  // Amount each in-flight mark-as-read actually subtracted, keyed by rollbackId.
  pendingReadRollbacks: {},
  // Bumped when a full fetch or an all-tab mark-all replaces the read snapshot.
  readCommitEpoch: 0,
  // rollbackId -> { epoch, ids, unreadIds } captured at MARK_AS_READ.START.
  pendingReadMeta: {},
  // Ids a successful mentions mark-all has already committed as read.
  suppressedReadIds: [],
  // Ids a failed mark-as-read put back. A later mark-all commit closes them again.
  reopenedReadIds: [],
  hasMore: false,
  nextFrom: null,
  isPageLoading: false,
  // Feed-fetch specific: `error` is shared with jobs/read/thumbnails actions.
  isLoadFailed: false,
  // True only when the failed fetch was a follow-up page, not a full refresh.
  isPageFailed: false,
};

export default function (state = defaultState, action) {
  const { type, payload, error } = action;

  switch (type) {
  /* Receive all unread notifications */
  case TYPES.NOTIFICATIONS.FETCH.START: {
    const append = Boolean(payload?.append);
    return {
      ...state,
      isLoaded: append ? state.isLoaded : false,
      isPageLoading: append,
      error: null,
      isLoadFailed: false,
      isPageFailed: false,
      notificationsUnreadCount: state.notificationsUnreadCount,
    };
  }
  case TYPES.NOTIFICATIONS.FETCH.COMPLETE: {
    const pageItems = payload.items || [];
    const items = payload.append
      ? helpers.appendFeedPage(state.items, pageItems)
      : pageItems;
    const receivedCount = Number.isInteger(payload.receivedCount)
      ? payload.receivedCount
      : pageItems.length;
    const cursorStuck = Boolean(
      payload.append && payload.pageCursor && payload.pageCursor === state.nextFrom,
    );
    return {
      ...state,
      isLoaded: true,
      isPageLoading: false,
      error: null,
      isLoadFailed: false,
      isPageFailed: false,
      items,
      hasMore: !cursorStuck && helpers.nextHasMore(receivedCount, items.length),
      nextFrom: Object.prototype.hasOwnProperty.call(payload, 'pageCursor')
        ? payload.pageCursor
        : state.nextFrom,
      newComments: payload.append
        ? helpers.mergeEventIdMaps(state.newComments, payload.newComments)
        : payload.newComments,
      newRevisions: payload.append
        ? helpers.mergeEventIdMaps(state.newRevisions, payload.newRevisions)
        : payload.newRevisions,
      notificationsUnreadCount: payload.append
        ? state.notificationsUnreadCount
        : (payload.notificationsUnreadCount || 0),
      // A full refresh replaces the feed and the badge with the server snapshot.
      // Drop badge rollbacks and bump the epoch so an older FAILED cannot reopen
      // cards the server already returned. Keep pendingReadMeta for that check.
      ...(payload.append ? {} : {
        pendingReadRollbacks: {},
        readCommitEpoch: (state.readCommitEpoch || 0) + 1,
        suppressedReadIds: [],
        reopenedReadIds: [],
      }),
    };
  }
  case TYPES.NOTIFICATIONS.FETCH.FAILED: {
    return {
      ...state,
      isLoaded: true,
      isPageLoading: false,
      // Stop paging so the failed cursor is not requested again in a loop.
      hasMore: false,
      error,
      isLoadFailed: true,
      // Keep `nextFrom` so a page retry resumes paging; a failed full refresh
      // must ignore that stale cursor and refetch the feed from the top.
      isPageFailed: Boolean(payload?.append),
      notificationsUnreadCount: state.notificationsUnreadCount,
    };
  }

  /* Receive notification */
  case TYPES.NOTIFICATIONS.ADD: {
    return {
      ...state,
      items: helpers.addNotification(state.items, payload.item),
      notificationsUnreadCount: state.notificationsUnreadCount + 1,
    };
  }

  case TYPES.NOTIFICATIONS.MERGE: {
    return {
      ...state,
      items: helpers.mergeNotification(state.items, payload.mergedItem),
    };
  }

  /* Receive ID of changed collections */
  case TYPES.NOTIFICATIONS.ADD_CHANGED_TAGS_IDS: {
    return {
      ...state,
      changedTags: _union(payload.ids, [...state.changedTags]).sort(),
    };
  }

  /* Mark all notifications in the active tab as read */
  case TYPES.NOTIFICATIONS.CLEAR.START: {
    const { updatedItems, updatedNewComments, updatedNewRevisions } = helpers.setNotificationRead(
      state.items,
      state.newComments,
      state.newRevisions,
      payload?.notificationIDS || [],
    );
    return {
      ...state,
      error: null,
      items: updatedItems,
      newComments: updatedNewComments,
      newRevisions: updatedNewRevisions,
    };
  }
  case TYPES.NOTIFICATIONS.CLEAR.COMPLETE: {
    const notificationsUnreadCount = Math.min(
      state.notificationsUnreadCount,
      Math.max(0, payload?.notificationsUnreadCount || 0),
    );
    const committedIds = payload?.committedNotificationIDS;
    const reopenedReadIds = state.reopenedReadIds || [];
    if (Array.isArray(committedIds)) {
      const committed = new Set(committedIds);
      const closedFeed = committedIds.length
        ? helpers.setNotificationRead(
          state.items,
          state.newComments,
          state.newRevisions,
          committedIds,
        )
        : null;
      return {
        ...state,
        error: null,
        notificationsUnreadCount,
        ...(closedFeed ? {
          items: closedFeed.updatedItems,
          newComments: closedFeed.updatedNewComments,
          newRevisions: closedFeed.updatedNewRevisions,
        } : {}),
        suppressedReadIds: _union(state.suppressedReadIds || [], committedIds),
        reopenedReadIds: reopenedReadIds.filter((id) => !committed.has(id)),
      };
    }
    const closedFeed = reopenedReadIds.length
      ? helpers.setNotificationRead(
        state.items,
        state.newComments,
        state.newRevisions,
        reopenedReadIds,
      )
      : null;
    return {
      ...state,
      error: null,
      notificationsUnreadCount,
      ...(closedFeed ? {
        items: closedFeed.updatedItems,
        newComments: closedFeed.updatedNewComments,
        newRevisions: closedFeed.updatedNewRevisions,
      } : {}),
      pendingReadRollbacks: {},
      readCommitEpoch: (state.readCommitEpoch || 0) + 1,
      suppressedReadIds: [],
      reopenedReadIds: [],
    };
  }
  case TYPES.NOTIFICATIONS.CLEAR.FAILED: {
    const {
      updatedItems,
      updatedNewComments,
      updatedNewRevisions,
    } = helpers.restoreNotificationUnread(
      state.items,
      state.newComments,
      state.newRevisions,
      payload?.newComments,
      payload?.newRevisions,
      payload?.notificationIDS || [],
    );
    return {
      ...state,
      error,
      items: updatedItems,
      newComments: updatedNewComments,
      newRevisions: updatedNewRevisions,
    };
  }

  /* Read notification */
  case TYPES.NOTIFICATIONS.MARK_AS_READ.START: {
    // Decrement here (not on COMPLETE): after START every targeted card is
    // already read, so COMPLETE can no longer tell which ones were unread.
    // Remember only the amount this START subtracted so FAILED can add it back.
    const unreadIds = helpers.unreadIdsInFeed(state.items, payload.notificationIDS || []);
    const { updatedItems, updatedNewComments, updatedNewRevisions } = helpers.setNotificationRead(
      state.items,
      state.newComments,
      state.newRevisions,
      payload.notificationIDS,
    );
    const applied = payload.withoutChangeCount
      ? 0
      : Math.min(unreadIds.length, state.notificationsUnreadCount);
    const rollbackId = payload.rollbackId;
    return {
      ...state,
      error: null,
      items: updatedItems,
      newComments: updatedNewComments,
      newRevisions: updatedNewRevisions,
      notificationsUnreadCount: state.notificationsUnreadCount - applied,
      ...(rollbackId ? {
        pendingReadMeta: {
          ...(state.pendingReadMeta || {}),
          [rollbackId]: {
            epoch: state.readCommitEpoch || 0,
            ids: payload.notificationIDS || [],
            unreadIds,
          },
        },
      } : {}),
      ...(applied > 0 && rollbackId ? {
        pendingReadRollbacks: {
          ...(state.pendingReadRollbacks || {}),
          [rollbackId]: applied,
        },
      } : {}),
    };
  }
  case TYPES.NOTIFICATIONS.MARK_AS_READ.COMPLETE: {
    const rollbackId = payload?.rollbackId;
    return {
      ...state,
      error: null,
      pendingReadRollbacks: dropPendingReadRollback(
        state.pendingReadRollbacks,
        rollbackId,
      ),
      pendingReadMeta: dropPendingReadRollback(state.pendingReadMeta, rollbackId),
    };
  }
  case TYPES.NOTIFICATIONS.MARK_AS_READ.FAILED: {
    // A later full fetch or all-tab mark-all bumps the epoch. That FAILED must
    // not reopen cards or add the stale decrement. A mentions mark-all only
    // suppresses the ids it committed.
    const rollbackId = payload?.rollbackId;
    const meta = rollbackId ? state.pendingReadMeta?.[rollbackId] : undefined;
    const superseded = Boolean(meta) && meta.epoch !== (state.readCommitEpoch || 0);
    const pendingReadRollbacks = dropPendingReadRollback(
      state.pendingReadRollbacks,
      rollbackId,
    );
    const pendingReadMeta = dropPendingReadRollback(state.pendingReadMeta, rollbackId);

    if (superseded) {
      return {
        ...state,
        error,
        pendingReadRollbacks,
        pendingReadMeta,
      };
    }

    const suppressed = new Set(state.suppressedReadIds || []);
    const ids = (payload?.notificationIDS || []).filter((id) => !suppressed.has(id));
    const pendingAmount = state.pendingReadRollbacks?.[rollbackId] || 0;
    const restored = meta
      ? Math.min(
        meta.unreadIds.filter((id) => !suppressed.has(id)).length,
        pendingAmount,
      )
      : pendingAmount;
    const restoredFeed = ids.length
      ? helpers.restoreNotificationUnread(
        state.items,
        state.newComments,
        state.newRevisions,
        payload?.newComments,
        payload?.newRevisions,
        ids,
      )
      : null;

    return {
      ...state,
      error,
      ...(restoredFeed ? {
        items: restoredFeed.updatedItems,
        newComments: restoredFeed.updatedNewComments,
        newRevisions: restoredFeed.updatedNewRevisions,
      } : {}),
      notificationsUnreadCount: state.notificationsUnreadCount + restored,
      pendingReadRollbacks,
      pendingReadMeta,
      ...(ids.length ? {
        reopenedReadIds: _union(state.reopenedReadIds || [], ids),
      } : {}),
    };
  }

  /* Get asset thumnails url */
  case TYPES.NOTIFICATIONS.GET_THUMBNAILS.START: {
    return {
      ...state,
      error: null,
      items: helpers.setThumbnailsField(state.items, payload.ids, 'isLoading', true),
    };
  }

  case TYPES.NOTIFICATIONS.GET_THUMBNAILS.COMPLETE: {
    return {
      ...state,
      error: null,
      items: helpers.setThumbnails(state.items, payload.thumbnails),
    };
  }

  case TYPES.NOTIFICATIONS.GET_THUMBNAILS.FAILED: {
    return {
      ...state,
      error,
    };
  }

  /* Receive ID of changed collections */
  case TYPES.NOTIFICATIONS.CLEAR_CHANGED_TAGS_IDS: {
    return {
      ...state,
      changedTags: [],
    };
  }

  /* Fetch async jobs status */
  case TYPES.NOTIFICATIONS.FETCH_JOBS.START: {
    return {
      ...state,
      isLoaded: false,
      error: null,
    };
  }
  case TYPES.NOTIFICATIONS.FETCH_JOBS.COMPLETE: {
    const statuses = payload.jobsStatus || {};
    return {
      ...state,
      isLoaded: true,
      error: null,
      jobsStatus: { ...state.jobsStatus, ...statuses },
    };
  }
  case TYPES.NOTIFICATIONS.FETCH_JOBS.FAILED: {
    return {
      ...state,
      isLoaded: true,
      error,
      jobsStatus: {},
    };
  }

  case TYPES.NOTIFICATIONS.INCREASE_UNREAD_PARAM: {
    const { paramName, eventId, assetId } = payload;
    const param = { ...state[paramName] };
    if (param[assetId]) {
      param[assetId].push(eventId);
    } else {
      param[assetId] = [eventId];
    }
    return {
      ...state,
      [paramName]: param,
    };
  }

  default: {
    return state;
  }
  }
}
